import React from 'react';
import PropTypes from 'prop-types';
import { anomalyReasonText } from '../../utils/anomalySignals';
import { Button, GapChip, IconButton, CloseIcon } from '../ui';
import { formatCurrency } from '../../utils/formatting';
import { aiPriceGap, aiPriceState } from '../../utils/aiPrice';
import { unitAgingTone } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { formatPctSigned } from '../../utils/lotValue';
import { matchesRetarget, unitDisplayName, unitYear } from '../../utils/inventoryView';
import { NOT_FOR_COMMITTED, isCommittedUnit } from '../../utils/committedUnit';
import DealStatusChip from './DealStatusChip';
import {
    HeatCell, RiskPill, formatLastQuote,
} from './InventoryTableV2';

const TYPE_LABEL = { N: 'New', U: 'Used' };

// Today's unit tabs (UnitDetailPane ids), in the order the cells used them.
export const PREVIEW_TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'ai', label: 'Intelligence' },
    { id: 'pricing', label: 'Pricing & Usage' },
    { id: 'pipeline', label: 'Pipeline' },
    { id: 'activity', label: 'Activity' },
];

// A sentence ends with a full stop.
const sentence = (text) => {
    const t = (text || '').trim();
    return t && !/[.!?]$/.test(t) ? `${t}.` : t;
};

// The verdict that already says the asking price is above the AI estimate.
const ABOVE_AI_VERDICT = 'Above AI Estimate';

// "Why it is flagged": the unit's verdict, an asking price well over the AI
// estimate, and a live quote / retarget call. Built only from today's fields.
// The gap sentence is left out when the verdict already says the same thing.
export const flagReasons = (unit, thresholds) => {
    const reasons = [];
    const flagged = unit.AnomalyVerdict && unit.AnomalySeverity && unit.AnomalySeverity !== 'normal';
    if (flagged) {
        reasons.push({ key: 'verdict', strong: `${unit.AnomalyVerdict}.`, text: sentence(anomalyReasonText(unit)) });
    }
    const state = aiPriceState(unit, thresholds);
    const saidByVerdict = flagged && unit.AnomalyVerdict === ABOVE_AI_VERDICT;
    if ((state === 'above' || state === 'wellAbove') && !saidByVerdict) {
        reasons.push({ key: 'gap', text: `Asking is ${formatPctSigned(aiPriceGap(unit))} over the AI estimate.` });
    }
    if (unit.HasQualifyingQuote) {
        reasons.push({ key: 'lq', text: 'An open quote is at or above the AI price, so call the buyer before changing the price.' });
    } else if (matchesRetarget(unit)) {
        reasons.push({ key: 'rt', text: 'Last quote is within 10% of asking: a retarget call.' });
    }
    return reasons;
};

// The preview's Sell-speed risk value.
const riskText = (unit, committed) => {
    if (committed) return <span className="inv2-muted">{NOT_FOR_COMMITTED}</span>;
    return unit.RiskTier ? <RiskPill unit={unit} /> : <span className="inv2-muted">Not scored</span>;
};

/**
 * Preview of one unit (I4): key figures, why it is flagged, "Open unit"
 * (Intelligence) and links into the unit on each of today's tabs, which
 * replace v1's per-cell tabs (D1 as changed by the user). Rendered beside the table, or inside a drawer
 * below 1400px (the page decides). The close button is omitted in the drawer,
 * which brings its own.
 */
function UnitPreview({
    unit, machineCatalog, hasOpenTask, readOnly, onClose, onOpen, onCreateTask, titleId,
}) {
    const { aging, gapThresholds } = useDealershipSettings();
    // A committed unit has a buyer: no sell-speed risk and no selling advice
    // (R2-B1); the unit was cleaned where it was loaded.
    const committed = isCommittedUnit(unit);
    const reasons = committed ? [] : flagReasons(unit, gapThresholds);
    const { name, original } = unitDisplayName(unit, machineCatalog);
    const meta = [TYPE_LABEL[unit.NewUsedStatus] || null, unit.CATEGORY, unit.Salesman].filter(Boolean).join(' · ');
    return (
        <div className="inv2-pv">
            <div className="inv2-pv-head">
                <div className="inv2-pv-top">
                    <span className="inv2-stock">#{unit.StockNumber}</span>
                    <span className={`inv2-age v2-tone-${unitAgingTone(unit, aging)}`} aria-hidden="true" />
                    <span className="inv2-muted inv2-pv-where">
                        {unit.BranchName ?? unit.LOCATION ?? '—'} · {unit.DaysInStock == null
                            ? 'Not yet received'
                            : `${unit.DaysInStock.toLocaleString('en-US')} days`}
                    </span>
                    {onClose && (
                        <IconButton label="Close preview" className="inv2-pv-close" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
                <h3 id={titleId} title={original !== name ? original : undefined}>
                    {name}
                    {unitYear(unit) && <span className="inv2-muted"> {unitYear(unit)}</span>}
                </h3>
                {(committed || unit.DealStatus === 'on_hold') && (
                    <div className="inv2-pv-deal"><DealStatusChip unit={unit} withVerb /></div>
                )}
                {meta && <span className="inv2-muted inv2-pv-meta">{meta}</span>}
            </div>

            <dl className="inv2-pv-grid">
                <div><dt>Asking</dt><dd className="inv2-pv-n">{unit.Best_Sell_Price > 0 ? formatCurrency(unit.Best_Sell_Price) : '—'}</dd></div>
                <div>
                    <dt>AI price</dt>
                    <dd className="inv2-pv-n">{aiPriceState(unit, gapThresholds) === 'none' ? '—' : formatCurrency(unit.PredictedPrice)}</dd>
                    {aiPriceState(unit, gapThresholds) !== 'none' && <dd><GapChip unit={unit} /></dd>}
                </div>
                <div><dt>True cost</dt><dd className="inv2-pv-n">{unit.CarryingCostBasis != null ? formatCurrency(unit.CarryingCostBasis) : '—'}</dd></div>
                <div><dt>Sell-speed risk</dt><dd>{riskText(unit, committed)}</dd></div>
                <div><dt>Interest</dt><dd><HeatCell unit={unit} withCountLabel /></dd></div>
                <div>
                    <dt>Last quote</dt>
                    <dd className="inv2-pv-lq">{unit.LastQuotedGapPct == null ? '—' : `${formatLastQuote(unit.LastQuotedGapPct)} vs ask`}</dd>
                </div>
            </dl>

            <div className="inv2-pv-why">
                <span className="v2-eyebrow">Why it is flagged</span>
                {committed && <p className="inv2-muted">{NOT_FOR_COMMITTED}</p>}
                {!committed && (reasons.length ? (
                    <p>
                        {reasons.map((r) => (
                            <span key={r.key}>
                                {r.strong && <b>{r.strong} </b>}
                                {r.text}
                                {' '}
                            </span>
                        ))}
                    </p>
                ) : <p>No open signals on this unit.</p>)}
                {hasOpenTask && <p><b>Open task</b> on this unit.</p>}
            </div>

            <div className="inv2-pv-links">
                <Button variant="primary" onClick={() => onOpen('ai')}>Open unit</Button>
                {/* v1's per-cell tabs (D1 as changed): each opens the unit on that tab. */}
                <div className="inv2-pv-tabs" role="group" aria-label="Open the unit on a tab">
                    {PREVIEW_TABS.map((t) => (
                        <Button key={t.id} size="sm" onClick={() => onOpen(t.id)}>
                            {t.label} <span aria-hidden="true">›</span>
                        </Button>
                    ))}
                </div>
                {!readOnly && (
                    <Button onClick={onCreateTask}>Create task <span aria-hidden="true">+</span></Button>
                )}
            </div>
        </div>
    );
}

UnitPreview.propTypes = {
    unit: PropTypes.object.isRequired,
    machineCatalog: PropTypes.object,
    hasOpenTask: PropTypes.bool,
    readOnly: PropTypes.bool,
    onClose: PropTypes.func,
    onOpen: PropTypes.func.isRequired,
    onCreateTask: PropTypes.func.isRequired,
    titleId: PropTypes.string,
};

export default UnitPreview;
